import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdEmail,
  MdSearch,
  MdRefresh,
  MdDelete,
  MdMarkEmailRead,
  MdMarkEmailUnread,
  MdReply,
  MdClose,
  MdAccessTime,
  MdPerson,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdSend,
} from 'react-icons/md';

export default function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalNewCount, setTotalNewCount] = useState(0);

  // Selected message for details & reply modal
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: search.trim() || undefined,
      };

      const res = await api.get('/contact/admin', { params });
      if (res.data?.success) {
        setMessages(res.data.data.messages || []);
        setTotalPages(res.data.data.pagination.totalPages || 1);
        setTotalCount(res.data.data.pagination.total || 0);
        setTotalNewCount(res.data.data.counts.totalNew || 0);
      }
    } catch (err) {
      console.error('Failed to load contact messages:', err);
      toast.error('Failed to load contact messages');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleStatusChange = async (id, newStatus, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await api.patch(`/contact/admin/${id}/status`, { status: newStatus });
      if (res.data?.success) {
        toast.success(`Marked as ${newStatus}`);
        setMessages((prev) =>
          prev.map((msg) => (msg.id === id ? { ...msg, status: newStatus } : msg))
        );
        if (selectedMessage?.id === id) {
          setSelectedMessage((prev) => ({ ...prev, status: newStatus }));
        }
        if (newStatus === 'read' || newStatus === 'replied') {
          setTotalNewCount((c) => Math.max(0, c - 1));
        }
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this contact message?')) {
      return;
    }

    try {
      const res = await api.delete(`/contact/admin/${id}`);
      if (res.data?.success) {
        toast.success('Message deleted');
        if (selectedMessage?.id === id) {
          setSelectedMessage(null);
        }
        fetchMessages();
      }
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const handleOpenMessage = (msg) => {
    setSelectedMessage(msg);
    setReplyText('');
    // Auto-mark as read if new
    if (msg.status === 'new') {
      handleStatusChange(msg.id, 'read');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    setReplySending(true);
    try {
      const res = await api.post(`/contact/admin/${selectedMessage.id}/reply`, {
        replyText: replyText.trim(),
      });
      if (res.data?.success) {
        toast.success('Reply sent successfully!');
        setSelectedMessage((prev) => ({ ...prev, status: 'replied' }));
        setMessages((prev) =>
          prev.map((m) => (m.id === selectedMessage.id ? { ...m, status: 'replied' } : m))
        );
        setReplyText('');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send reply. (Check if SMTP_PASS is configured in .env)';
      toast.error(msg);
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MdEmail style={{ color: '#dc2626' }} /> Contact Messages
            {totalNewCount > 0 && (
              <span style={{
                background: '#dc2626',
                color: '#fff',
                fontSize: '13px',
                padding: '3px 10px',
                borderRadius: '12px',
                fontWeight: '600'
              }}>
                {totalNewCount} New
              </span>
            )}
          </h1>
          <p style={{ margin: '6px 0 0 0', color: 'var(--text-secondary, #64748b)', fontSize: '14px' }}>
            Inquiries and messages submitted by visitors through the website contact form
          </p>
        </div>

        <button
          onClick={fetchMessages}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: 'var(--card-bg, #fff)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            color: 'var(--text-primary, #1e293b)'
          }}
        >
          <MdRefresh /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'var(--card-bg, #fff)', padding: '18px', borderRadius: '12px', border: '1px solid var(--border-color, #e2e8f0)' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary, #64748b)', fontWeight: '600', textTransform: 'uppercase' }}>Total Inquiries</div>
          <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '6px', color: 'var(--text-primary, #0f172a)' }}>{totalCount}</div>
        </div>
        <div style={{ background: 'var(--card-bg, #fff)', padding: '18px', borderRadius: '12px', border: '1px solid var(--border-color, #e2e8f0)' }}>
          <div style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600', textTransform: 'uppercase' }}>Unread / New</div>
          <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '6px', color: '#dc2626' }}>{totalNewCount}</div>
        </div>
        <div style={{ background: 'var(--card-bg, #fff)', padding: '18px', borderRadius: '12px', border: '1px solid var(--border-color, #e2e8f0)' }}>
          <div style={{ fontSize: '13px', color: '#2563eb', fontWeight: '600', textTransform: 'uppercase' }}>Status</div>
          <div style={{ fontSize: '14px', marginTop: '10px', color: 'var(--text-secondary, #64748b)' }}>
            Nodemailer & MySQL Active
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        background: 'var(--card-bg, #fff)',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid var(--border-color, #e2e8f0)',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['all', 'new', 'read', 'replied'].map((st) => (
            <button
              key={st}
              onClick={() => { setStatusFilter(st); setPage(1); }}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                border: 'none',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                background: statusFilter === st ? '#dc2626' : 'var(--bg-secondary, #f1f5f9)',
                color: statusFilter === st ? '#fff' : 'var(--text-secondary, #475569)',
                transition: 'all 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {st} {st === 'new' && totalNewCount > 0 ? `(${totalNewCount})` : ''}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '280px' }}>
          <div style={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <MdSearch style={{ position: 'absolute', left: '12px', color: '#94a3b8', fontSize: '18px' }} />
            <input
              type="text"
              placeholder="Search by name, email, message..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{
                width: '100%',
                padding: '8px 12px 8px 38px',
                borderRadius: '8px',
                border: '1px solid var(--border-color, #e2e8f0)',
                background: 'var(--input-bg, #f8fafc)',
                color: 'var(--text-primary, #0f172a)',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div style={{
        background: 'var(--card-bg, #fff)',
        borderRadius: '12px',
        border: '1px solid var(--border-color, #e2e8f0)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <MdEmail style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
            <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600' }}>No contact messages found</h3>
            <p style={{ margin: 0, fontSize: '14px' }}>There are no messages matching your search or filter.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: 'var(--table-header-bg, #f8fafc)', borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)', width: '60px' }}>#</th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)' }}>Sender</th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)' }}>Subject</th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)', width: '35%' }}>Message</th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)', width: '140px' }}>Date</th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)', width: '100px' }}>Status</th>
                  <th style={{ padding: '14px 16px', fontWeight: '600', color: 'var(--text-secondary, #475569)', width: '140px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => {
                  const isNew = msg.status === 'new';
                  return (
                    <tr
                      key={msg.id}
                      onClick={() => handleOpenMessage(msg)}
                      style={{
                        borderBottom: '1px solid var(--border-color, #f1f5f9)',
                        cursor: 'pointer',
                        background: isNew ? 'rgba(220, 38, 38, 0.04)' : 'transparent',
                        fontWeight: isNew ? '600' : 'normal',
                        transition: 'background 0.15s'
                      }}
                    >
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary, #64748b)' }}>
                        #{msg.id}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ color: 'var(--text-primary, #0f172a)' }}>{msg.name}</div>
                        <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: 'normal' }}>{msg.email}</div>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--text-primary, #0f172a)' }}>
                        {msg.subject || 'General Inquiry'}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary, #475569)', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {msg.message}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--text-secondary, #64748b)', fontSize: '13px' }}>
                        {new Date(msg.createdAt || msg.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'capitalize',
                          background:
                            msg.status === 'new' ? '#fee2e2' :
                            msg.status === 'replied' ? '#dcfce7' : '#e0e7ff',
                          color:
                            msg.status === 'new' ? '#991b1b' :
                            msg.status === 'replied' ? '#166534' : '#3730a3',
                        }}>
                          {msg.status}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          {msg.status === 'new' ? (
                            <button
                              title="Mark as Read"
                              onClick={(e) => handleStatusChange(msg.id, 'read', e)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#64748b',
                                fontSize: '18px',
                                padding: '4px',
                              }}
                            >
                              <MdMarkEmailRead />
                            </button>
                          ) : (
                            <button
                              title="Mark as New"
                              onClick={(e) => handleStatusChange(msg.id, 'new', e)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#64748b',
                                fontSize: '18px',
                                padding: '4px',
                              }}
                            >
                              <MdMarkEmailUnread />
                            </button>
                          )}
                          <button
                            title="Delete Message"
                            onClick={(e) => handleDelete(msg.id, e)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#dc2626',
                              fontSize: '18px',
                              padding: '4px',
                            }}
                          >
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderTop: '1px solid var(--border-color, #e2e8f0)',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary, #64748b)' }}>
              Showing Page {page} of {totalPages} ({totalCount} total messages)
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  background: 'var(--card-bg, #fff)',
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  opacity: page <= 1 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <MdChevronLeft /> Prev
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  background: 'var(--card-bg, #fff)',
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                  opacity: page >= totalPages ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                Next <MdChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Message Detail & Reply Modal */}
      {selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg, #fff)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '680px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            border: '1px solid var(--border-color, #e2e8f0)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-color, #e2e8f0)'
            }}>
              <div>
                <span style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'capitalize',
                  background:
                    selectedMessage.status === 'new' ? '#fee2e2' :
                    selectedMessage.status === 'replied' ? '#dcfce7' : '#e0e7ff',
                  color:
                    selectedMessage.status === 'new' ? '#991b1b' :
                    selectedMessage.status === 'replied' ? '#166534' : '#3730a3',
                  marginBottom: '6px'
                }}>
                  Status: {selectedMessage.status}
                </span>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-primary, #0f172a)' }}>
                  {selectedMessage.subject || 'General Inquiry'}
                </h3>
              </div>
              <button
                onClick={() => setSelectedMessage(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#94a3b8'
                }}
              >
                <MdClose />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Sender Details */}
              <div style={{
                background: 'var(--bg-secondary, #f8fafc)',
                padding: '16px',
                borderRadius: '10px',
                marginBottom: '20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '12px',
                fontSize: '14px'
              }}>
                <div>
                  <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600' }}>SENDER</div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary, #0f172a)', marginTop: '2px' }}>{selectedMessage.name}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600' }}>EMAIL</div>
                  <div style={{ color: '#2563eb', marginTop: '2px' }}>
                    <a href={`mailto:${selectedMessage.email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                      {selectedMessage.email}
                    </a>
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600' }}>DATE & TIME</div>
                  <div style={{ color: 'var(--text-primary, #0f172a)', marginTop: '2px' }}>
                    {new Date(selectedMessage.createdAt || selectedMessage.created_at).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Message Content
                </div>
                <div style={{
                  padding: '16px',
                  background: 'var(--bg-primary, #ffffff)',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  borderRadius: '10px',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: 'var(--text-primary, #1e293b)',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedMessage.message}
                </div>
              </div>

              {/* Reply Section */}
              <div style={{ borderTop: '1px solid var(--border-color, #e2e8f0)', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary, #0f172a)' }}>
                    Reply to {selectedMessage.name}
                  </label>
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${encodeURIComponent(selectedMessage.subject || 'Inquiry to Udayam Digital')}`}
                    style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <MdReply /> Open in Mail App
                  </a>
                </div>

                <textarea
                  rows="4"
                  placeholder="Type your reply to send directly to their email..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    background: 'var(--input-bg, #f8fafc)',
                    color: 'var(--text-primary, #0f172a)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', gap: '10px' }}>
                  <button
                    onClick={() => handleStatusChange(selectedMessage.id, selectedMessage.status === 'replied' ? 'read' : 'replied')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--text-secondary, #64748b)'
                    }}
                  >
                    Mark as {selectedMessage.status === 'replied' ? 'Read' : 'Replied'}
                  </button>

                  <button
                    onClick={handleSendReply}
                    disabled={replySending || !replyText.trim()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#dc2626',
                      color: '#fff',
                      cursor: replySending || !replyText.trim() ? 'not-allowed' : 'pointer',
                      opacity: replySending || !replyText.trim() ? 0.6 : 1,
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    <MdSend /> {replySending ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
